import {VALIDATE_CONFIG_RULES_RUNNING} from 'common/types';
import DynamicComponentActions from 'go/actions/dynamicComponent';
import {call, take, select, fork, put, race} from 'redux-saga/effects';
import {delay} from 'redux-saga';
import _ from 'lodash';
import ruleSet from 'framework/validation/rules';
import {isFemaUser, countDependenciesToLoad} from 'common/utils';
import {compileContent} from 'common/utils';

/**
 * Creates a debounced saga generator function that only executes once per microtask queue cycle.
 * @template T
 * @param {(...args: any[]) => Generator<any, T, any>} sagaGen - The saga generator function to debounce
 * @returns {(...args: any[]) => Generator<any, T, any>} - Debounced saga generator function
 */
export function microDebounceSaga(sagaGen) {
  /*
   * scheduleCounter will keep track of how many "overlapping calls" to this method so that
   * we only run the sagaGen passed to use when we are the last one placed into the queue of
   * microtask calls.  The first and subsequent calls will decrement counter until we reach the last func call,
   * which will decrement and that brings scheduledCounter down to 0.
   * That is, ignore all calls except for the last one.
   */
  let scheduledCounter = 0;
  let lastArgs = null;
  return function* microDebounceSagaInner(...args) {
    // Store the latest arguments
    lastArgs = args;
    scheduledCounter++;
    /*
     * Go back to end of microtask queue once for the saveEntitySaga, and once for validation saga
     */
    const backOfMicroqueueLineCount = 2;
    for (let i = 0; i < backOfMicroqueueLineCount; i++) {
      yield new Promise((resolve) => resolve());
    }
    scheduledCounter--;
    if (0 === scheduledCounter) {
      yield* sagaGen.apply(this, lastArgs);
    }
  };
}

// Create debounced version
export const calculateAndSaveValidationsDebounced = microDebounceSaga(
  calculateAndSaveValidations
);

export const selectAsyncValidationToggle = (state) =>
  _.includes(_.get(state, 'toggles.toggles', []), 'asyncValidations');

export default function* saveEntitySaga() {
  const asyncValidations = yield select(selectAsyncValidationToggle);
  const action = yield take(VALIDATE_CONFIG_RULES_RUNNING);
  if (asyncValidations) {
    yield fork(calculateAndSaveValidationsDebounced, action);
  } else {
    yield fork(calculateAndSaveValidations, action);
  }
  yield call(saveEntitySaga);
}

export const runRule = (validation, dynamicState = {}) => {
  const {
    dependencies: dependencyPaths,
    rule: ruleName,
    context: passedInValues,
    templatedContext,
  } = validation;
  const rule = _.get(ruleSet, ruleName);
  let context = {};
  _.forEach(dependencyPaths, (dep) => {
    // should just be one with our dependencies format [{ key: value }, { key: value }]
    _.forEach(dep, (paths, name) => {
      if (_.isArray(paths)) {
        _.set(context, name, []);
        _.each(paths, (myDep) => {
          let path = compileContent(myDep, {
            ...dynamicState,
          });
          const pathValue = _.get(dynamicState, path);
          context[name].push(pathValue);
        });
      } else {
        let compiledPath = compileContent(paths, {
          ...dynamicState,
        });
        _.set(context, name, _.get(dynamicState, compiledPath));
      }
    });
  });
  context.validationContext = dynamicState;
  _.forEach(passedInValues, (pair) => {
    context = {
      ...context,
      ...pair,
    };
  });
  _.forEach(templatedContext, (pair) => {
    const key = _.get(_.keys(pair), 0, 'invalidKey');
    const value = _.get(_.values(pair), 0, '');
    const templatedValue = compileContent(value, {
      ...dynamicState,
      ...context,
    });

    context[key] = templatedValue;
  });
  return rule(context);
};

export function* calculateAndSaveValidations(action) {
  const state = yield select();
  const toggles = _.get(state, 'toggles.toggles');
  const dynamicState = state.dynamicComponent;
  const dependenciesToLoad = countDependenciesToLoad(
    _.get(dynamicState, 'config.payload.dependencies', _.get('config.dependencies', [])),
    isFemaUser(_.get(state, 'auth.permissions')),
    toggles
  );
  const areDependenciesLoaded =
    dependenciesToLoad <= _.get(dynamicState, 'context.numLoadedSuccessfully', 0);
  if (areDependenciesLoaded) {
    const configValidations = _.get(
      dynamicState,
      'validationErrors.overrideValidations',
      _.get(
        dynamicState,
        'config.validations',
        _.get(dynamicState, 'config.payload.validations')
      )
    );
    let allValid = true;
    const validationResults = {};
    /** Dependencies and Context share a namespace **/
    _.forEach(configValidations, (validationSet, validationId) => {
      let validationsForId = _.map(validationSet, (validation) => {
        let result;
        // we want the validation to fail, not just error out
        try {
          result = runRule(validation, dynamicState);
        } catch (err) {
          console.error(err);
          result = {
            isValid: false,
            runtimeError: true,
            runtimeErrorMessage: _.toString(err),
          };
        }
        allValid = allValid && result.isValid;
        return result;
      });
      _.set(validationResults, validationId, validationsForId);
    });

    yield put(DynamicComponentActions.saveValidationResults(validationResults, allValid));
  }
}
